import { useTranslation } from 'react-i18next';

import { idAsString } from './projectSites';
import { useSelectedProject } from './selectedProject';

import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * The project, chosen once for the whole of CBOS.
 *
 * It sits above the list of screens rather than inside any of them, because that is what it governs:
 * every screen below shows this project, and a control that belongs to all of them belongs to none
 * of them in particular. Put on a screen — as it was, twice — it reads as that screen's setting, and
 * the reader is entitled to wonder what the other screen is showing.
 *
 * Nothing is stored here. The choice is in the address; this only offers it.
 */

/** Radix will not take an empty string as a value, and "no project" needs one. */
const NONE = '__none__';

export default function CbosProjectPicker() {
    const { t } = useTranslation();
    const { projectId, projects, loading, select } = useSelectedProject();

    return (
        <div className="flex flex-col gap-1.5 px-2 py-2">
            <Label htmlFor="cbosProject" className="text-xs text-muted-foreground">
                {t('portfolioDashboard.objectSelection.project')}
            </Label>

            <Select
                value={projectId ?? NONE}
                disabled={loading}
                onValueChange={(value) => select(value === NONE ? null : value)}
            >
                <SelectTrigger id="cbosProject" className="w-full">
                    <SelectValue placeholder={t('portfolioDashboard.objectSelection.selectProject')} />
                </SelectTrigger>
                <SelectContent>
                    {/* Choosing nothing is a choice: it is how one gets back to a screen that is not
                        about a particular project. */}
                    <SelectItem value={NONE}>{t('portfolioDashboard.objectSelection.noProject')}</SelectItem>
                    {projects.map((project) => {
                        const key = idAsString(project._id) ?? '';

                        return (
                            <SelectItem key={key} value={key}>
                                {project.name || key}
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
        </div>
    );
}
